import { useSeo } from "@/hooks/useSeo";
import LegalLayout, { LegalSection, ReviewNote } from "@/components/LegalLayout";
import { business } from "@/data/site";

const Privacy = () => {
  useSeo({ title: `Privacy Policy | ${business.name}`, description: `How ${business.name} collects, uses, and protects your personal information.` });
  return (
    <LegalLayout eyebrow="Legal" title="Privacy Policy" updated="June 2026">
      <p className="text-[#475569] leading-relaxed">{business.name} ("we", "us", "our") respects your privacy. This Privacy Policy explains what information we collect through this website, how we use it, and your choices.</p>
      <ReviewNote>Have this policy reviewed for compliance with applicable laws (e.g., New York and federal privacy regulations) before publishing.</ReviewNote>

      <LegalSection heading="1. Information We Collect">
        <p>We may collect the following information when you interact with our website:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Contact forms:</strong> name, email address, phone number, and any details you share.</li>
          <li><strong>Estimate request forms:</strong> service type, project details, and contact information.</li>
          <li><strong>Analytics tools:</strong> aggregated, non-identifying usage data such as pages visited and device type.</li>
          <li><strong>Cookies & similar technologies:</strong> data used to improve site functionality and experience.</li>
          <li><strong>Call tracking (if applicable):</strong> phone numbers and call details when you contact us.</li>
        </ul>
      </LegalSection>
      <LegalSection heading="2. How We Use Your Information">
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Respond to your inquiries and provide estimates.</li>
          <li>Schedule and deliver requested services.</li>
          <li>Improve our website, services, and customer experience.</li>
          <li>Communicate with you about your requests and, where permitted, relevant offers.</li>
        </ul>
      </LegalSection>
      <LegalSection heading="3. Cookies & Analytics">
        <p>We may use cookies and third-party analytics tools to understand how visitors use our site. You can control cookies through your browser settings. Disabling cookies may affect some website features.</p>
        <ReviewNote>List the specific analytics and tracking tools the client actually uses (e.g., Google Analytics, Meta Pixel, call tracking provider).</ReviewNote>
      </LegalSection>
      <LegalSection heading="4. How We Share Information">
        <p>We do not sell your personal information. We may share it with trusted service providers who help us operate our business (such as scheduling, communications, or analytics), and as required by law.</p>
      </LegalSection>
      <LegalSection heading="5. Data Security">
        <p>We take reasonable measures to protect your information. However, no method of transmission over the internet is completely secure, and we cannot guarantee absolute security.</p>
      </LegalSection>
      <LegalSection heading="6. Your Privacy Choices">
        <p>You may request to access, update, or delete your personal information, or opt out of marketing communications, by contacting us using the details below.</p>
      </LegalSection>
      <LegalSection heading="7. Children's Privacy">
        <p>Our website is not directed to children under 13, and we do not knowingly collect their personal information.</p>
      </LegalSection>
      <LegalSection heading="8. Contact Us About Privacy">
        <p>For privacy requests or questions, contact us at <a href={business.emailHref} className="text-[#059669] font-semibold hover:underline">{business.email}</a> or {business.phoneDisplay}.</p>
      </LegalSection>
    </LegalLayout>
  );
};

export default Privacy;
